import { useState, useEffect } from 'react';
import type { CreateUserData, UpdateUserData, User } from '../../api/users';

interface UserFormProps {
  user?: User;
  onSubmit: (data: CreateUserData | UpdateUserData) => Promise<void>;
  onCancel: () => void;
  isEdit?: boolean;
}

export function UserForm({ user, onSubmit, onCancel, isEdit = false }: UserFormProps) {
  const [username, setUsername] = useState(user?.username ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [role, setRole] = useState<'user' | 'admin'>(user?.role ?? 'user');
  const [maxBudget, setMaxBudget] = useState(user?.maxBudget?.toString() ?? '');
  const [tpmLimit, setTpmLimit] = useState(user?.tpmLimit?.toString() ?? '');
  const [rpmLimit, setRpmLimit] = useState(user?.rpmLimit?.toString() ?? '');
  const [allowedCidrs, setAllowedCidrs] = useState(user?.allowedCidrs?.join(', ') ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setUsername(user.username);
      setEmail(user.email);
      setDisplayName(user.displayName);
      setRole(user.role);
      setMaxBudget(user.maxBudget?.toString() ?? '');
      setTpmLimit(user.tpmLimit?.toString() ?? '');
      setRpmLimit(user.rpmLimit?.toString() ?? '');
      setAllowedCidrs(user.allowedCidrs?.join(', ') ?? '');
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const cidrs = allowedCidrs
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);

      if (isEdit) {
        const data: UpdateUserData = {
          email,
          displayName,
          role,
          maxBudget: maxBudget ? parseFloat(maxBudget) : undefined,
          tpmLimit: tpmLimit ? parseInt(tpmLimit, 10) : undefined,
          rpmLimit: rpmLimit ? parseInt(rpmLimit, 10) : undefined,
          allowedCidrs: cidrs.length > 0 ? cidrs : undefined,
        };
        await onSubmit(data);
      } else {
        const data: CreateUserData = {
          username,
          email,
          displayName,
          role,
          maxBudget: maxBudget ? parseFloat(maxBudget) : undefined,
          tpmLimit: tpmLimit ? parseInt(tpmLimit, 10) : undefined,
          rpmLimit: rpmLimit ? parseInt(rpmLimit, 10) : undefined,
          allowedCidrs: cidrs.length > 0 ? cidrs : undefined,
        };
        await onSubmit(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="alert alert--error" style={{ marginBottom: 16 }}>
          <span>{error}</span>
        </div>
      )}

      {!isEdit && (
        <div className="modal__field">
          <label htmlFor="user-username">Username</label>
          <input
            id="user-username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="jdoe"
            required
            autoFocus
          />
        </div>
      )}

      <div className="modal__field">
        <label htmlFor="user-email">Email</label>
        <input
          id="user-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          required
        />
      </div>

      <div className="modal__field">
        <label htmlFor="user-display-name">Display Name</label>
        <input
          id="user-display-name"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Jane Doe"
        />
      </div>

      <div className="modal__field">
        <label htmlFor="user-role">Role</label>
        <select
          id="user-role"
          className="modal__select"
          value={role}
          onChange={(e) => setRole(e.target.value as 'user' | 'admin')}
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <div className="modal__field">
        <label htmlFor="user-budget">Max Budget ($)</label>
        <input
          id="user-budget"
          type="number"
          step="0.01"
          min="0"
          value={maxBudget}
          onChange={(e) => setMaxBudget(e.target.value)}
          placeholder="100.00"
        />
      </div>

      <div className="modal__field">
        <label htmlFor="user-tpm">TPM Limit</label>
        <input
          id="user-tpm"
          type="number"
          min="0"
          value={tpmLimit}
          onChange={(e) => setTpmLimit(e.target.value)}
          placeholder="100000"
        />
      </div>

      <div className="modal__field">
        <label htmlFor="user-rpm">RPM Limit</label>
        <input
          id="user-rpm"
          type="number"
          min="0"
          value={rpmLimit}
          onChange={(e) => setRpmLimit(e.target.value)}
          placeholder="60"
        />
      </div>

      <div className="modal__field">
        <label htmlFor="user-cidrs">Allowed CIDRs (comma-separated)</label>
        <input
          id="user-cidrs"
          type="text"
          value={allowedCidrs}
          onChange={(e) => setAllowedCidrs(e.target.value)}
          placeholder="10.0.0.0/8, 172.16.0.0/12"
        />
      </div>

      <div className="modal__actions">
        <button type="button" className="modal__cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="modal__submit" disabled={submitting}>
          {submitting ? 'Saving...' : isEdit ? 'Update User' : 'Create User'}
        </button>
      </div>
    </form>
  );
}
